import React, { useState, useEffect, useCallback } from 'react';
import { productService, extractErrorDetails } from '../services/api';
import BarcodeScanner from './BarcodeScanner';
import ErrorDisplay from './ErrorDisplay';

function ProductManagement() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorTrace, setErrorTrace] = useState(null);
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    codigoBarras: '',
    codigoExterno: '',
    nombre: '',
    descripcion: '',
    activo: true
  });
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 0,
    size: 20,
    totalPages: 0,
    totalElements: 0
  });

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setErrorTrace(null);
      const filterParams = {
        search: searchQuery,
        page: pagination.page,
        size: pagination.size,
        sortBy: 'nombre',
        sortDirection: 'asc'
      };
      
      const response = await productService.getProductsPaginated(filterParams);
      if (response.success) {
        const pageData = response.data;
        setProducts(pageData.content || []);
        setFilteredProducts(pageData.content || []);
        setPagination(prev => ({
          ...prev,
          totalPages: pageData.totalPages,
          totalElements: pageData.totalElements
        }));
      } else {
        setError(response.message || 'Error al cargar los productos');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al cargar los productos');
      setError(message);
      setErrorTrace(trace);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, searchQuery]);

  useEffect(() => {
    fetchProducts();
  }, [pagination.page, searchQuery, fetchProducts]);

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setPagination(prev => ({ ...prev, page: 0 })); // Reset to first page on search
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorTrace(null);
    setSuccess('');

    try {
      const submitData = {
        ...formData
      };
      let response;
      if (editingProduct) {
        response = await productService.updateProduct(editingProduct.id, submitData);
      } else {
        response = await productService.createProduct(submitData);
      }

      if (response.success) {
        setSuccess(editingProduct ? 'Producto actualizado exitosamente' : 'Producto creado exitosamente');
        resetForm();
        fetchProducts();
      } else {
        setError(response.message || 'Error al guardar el producto');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al guardar el producto');
      setError(message);
      setErrorTrace(trace);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      codigoBarras: product.codigoBarras,
      codigoExterno: product.codigoExterno || '',
      nombre: product.nombre,
      descripcion: product.descripcion || '',
      activo: product.activo
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Está seguro de eliminar este producto?')) {
      try {
        setError('');
        setErrorTrace(null);
        const response = await productService.deleteProduct(id);
        if (response.success) {
          setSuccess('Producto eliminado exitosamente');
          fetchProducts();
        } else {
          setError(response.message || 'Error al eliminar el producto');
          setErrorTrace(response.errorTrace || null);
        }
      } catch (err) {
        const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al eliminar el producto');
        setError(message);
        setErrorTrace(trace);
      }
    }
  };

  const handleScan = (barcode) => {
    setFormData(prev => ({
      ...prev,
      codigoBarras: barcode
    }));
    setShowScanner(false);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      codigoBarras: '',
      codigoExterno: '',
      nombre: '',
      descripcion: '',
      activo: true
    });
    setEditingProduct(null);
    setShowForm(false);
  };

  const openNewProductForm = () => {
    resetForm();
    setShowForm(true);
  };

  return (
    <div className="product-management">
      <div className="section-header">
        <h2>Gestión de Productos</h2>
        <div className="header-actions">
          <button onClick={openNewProductForm} className="btn btn-primary">
            + Nuevo Producto
          </button>
          <button onClick={() => setShowScanner(true)} className="btn btn-secondary">
            📷 Escanear Código
          </button>
        </div>
      </div>

      {/* Search Filter */}
      <div className="search-filter-container">
        <div className="input-with-button">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Buscar por código de barras o nombre..."
            className="search-input"
          />
          {searchQuery && (
            <button 
              type="button" 
              onClick={() => handleSearchChange('')}
              className="btn btn-icon"
              title="Limpiar búsqueda"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <ErrorDisplay error={error} errorTrace={errorTrace} />
      {success && <p className="success-message">{success}</p>}

      {showScanner && (
        <BarcodeScanner
          onScan={handleScan}
          onClose={() => setShowScanner(false)}
        />
      )}

      {showForm && (
        <div className="product-form-container">
          <form onSubmit={handleSubmit} className="product-form">
            <h3>{editingProduct ? 'Editar Producto' : 'Nuevo Producto'}</h3>
            
            <div className="input-group">
              <label htmlFor="codigoBarras">Código de Barras</label>
              <div className="input-with-button">
                <input
                  type="text"
                  id="codigoBarras"
                  name="codigoBarras"
                  value={formData.codigoBarras}
                  onChange={handleChange}
                  placeholder="Código de barras"
                  required
                />
                <button 
                  type="button" 
                  onClick={() => setShowScanner(true)}
                  className="btn btn-icon"
                >
                  📷
                </button>
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="codigoExterno">Código Externo</label>
              <input
                type="text"
                id="codigoExterno"
                name="codigoExterno"
                value={formData.codigoExterno}
                onChange={handleChange}
                placeholder="Código externo (opcional)"
              />
            </div>

            <div className="input-group">
              <label htmlFor="nombre">Nombre</label>
              <input
                type="text"
                id="nombre"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                placeholder="Nombre del producto"
                required
              />
            </div>

            <div className="input-group">
              <label htmlFor="descripcion">Descripción</label>
              <textarea
                id="descripcion"
                name="descripcion"
                value={formData.descripcion}
                onChange={handleChange}
                placeholder="Descripción del producto"
                rows="3"
              />
            </div>

            {editingProduct && (
              <div className="input-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="activo"
                    checked={formData.activo}
                    onChange={handleChange}
                  />
                  Producto activo
                </label>
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingProduct ? 'Actualizar' : 'Crear'}
              </button>
              <button type="button" onClick={resetForm} className="btn btn-secondary">
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <p>Cargando productos...</p>
      ) : products.length === 0 ? (
        <div className="empty-state">
          <p>No hay productos registrados</p>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="empty-state">
          <p>No se encontraron productos con ese criterio de búsqueda</p>
        </div>
      ) : (
        <>
          <div className="products-table">
            <table>
              <thead>
                <tr>
                  <th>Código de Barras</th>
                  <th>Código Externo</th>
                  <th>Nombre</th>
                  <th>Descripción</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => (
                  <tr key={product.id} className={!product.activo ? 'inactive-row' : ''}>
                    <td>{product.codigoBarras}</td>
                    <td>{product.codigoExterno || '-'}</td>
                    <td>{product.nombre}</td>
                    <td>{product.descripcion || '-'}</td>
                    <td>
                      <span className={`status-badge ${product.activo ? 'status-active' : 'status-inactive'}`}>
                        {product.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button 
                        onClick={() => handleEdit(product)}
                        className="btn btn-small"
                      >
                        Editar
                      </button>
                      <button 
                        onClick={() => handleDelete(product.id)}
                        className="btn btn-small btn-danger"
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination controls */}
          {pagination.totalPages > 1 && (
            <div className="pagination-controls" style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
              <button
                onClick={() => handlePageChange(0)}
                disabled={pagination.page === 0}
                className="btn btn-sm"
              >
                {'<<'}
              </button>
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 0}
                className="btn btn-sm"
              >
                {'<'}
              </button>
              <span>
                Página {pagination.page + 1} de {pagination.totalPages}
                {' '}({pagination.totalElements} total)
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages - 1}
                className="btn btn-sm"
              >
                {'>'}
              </button>
              <button
                onClick={() => handlePageChange(pagination.totalPages - 1)}
                disabled={pagination.page >= pagination.totalPages - 1}
                className="btn btn-sm"
              >
                {'>>'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ProductManagement;
